import type {ToolUserRequestedMessage, LogEntry, SessionState} from '../../types/session'
import {SessionState as SessionStateValues} from '../../types/session'
import type {OptimisticPromptResponse} from '../../contexts/OptimisticLogsContext'
import {findToolResponse, formatOptimisticResponse} from './prompt-log-utils'
import {AskUserLogEntry} from './AskUserLogEntry'
import {PlanApprovalLogEntry} from './PlanApprovalLogEntry'
import {PermissionLogEntry} from './PermissionLogEntry'
import {LoadingMessage} from './LoadingMessage'

interface InteractivePromptLogEntryProps {
  prompt: ToolUserRequestedMessage
  abortedToolCallIds: Set<string>
  logsData?: LogEntry[]
  optimisticResponse?: OptimisticPromptResponse
  sessionState?: SessionState
}

/**
 * Renders an interactive prompt (ask_user, permission_request, exit_plan_mode)
 * in the session logs timeline. Resolves the response centrally and delegates
 * to the appropriate specialized component.
 */
export function InteractivePromptLogEntry({
  prompt,
  abortedToolCallIds,
  logsData,
  optimisticResponse,
  sessionState,
}: InteractivePromptLogEntryProps) {
  const {toolCallId, toolName} = prompt

  const serverResponse = findToolResponse(toolCallId, logsData)
  const hasOptimistic = optimisticResponse !== undefined && optimisticResponse.status === 'pending'
  const optimisticAnswer = hasOptimistic
    ? formatOptimisticResponse(optimisticResponse.response, optimisticResponse.commandType)
    : undefined
  const resolvedAnswer = serverResponse ?? optimisticAnswer
  const isAborted = !resolvedAnswer && abortedToolCallIds.has(toolCallId)

  // Don't show "Resuming session…" for CLI sessions (which don't pause; `fromPausedSession` is false there).
  const stillPaused = sessionState === undefined || sessionState === SessionStateValues.WaitingForUser
  const showResumingIndicator =
    hasOptimistic &&
    !serverResponse &&
    optimisticResponse?.inferred !== true &&
    optimisticResponse?.fromPausedSession === true &&
    stillPaused

  let body: React.ReactNode = null
  switch (toolName) {
    case 'ask_user':
      body = <AskUserLogEntry prompt={prompt} resolvedAnswer={resolvedAnswer} isAborted={isAborted} />
      break
    case 'exit_plan_mode':
      body = <PlanApprovalLogEntry prompt={prompt} resolvedAnswer={resolvedAnswer} isAborted={isAborted} />
      break
    case 'permission_request':
      body = <PermissionLogEntry prompt={prompt} resolvedAnswer={resolvedAnswer} isAborted={isAborted} />
      break
    default:
      body = null
  }

  if (!body && !showResumingIndicator) {
    return null
  }

  return (
    <>
      {body}
      {showResumingIndicator && (
        <div role="status">
          <LoadingMessage message="Resuming session…" />
        </div>
      )}
    </>
  )
}
